// Anti-Inflammatory Foods Database
// 115 scientifically-validated foods with evidence-based scoring
// Version 1.1 - December 2024

// Evidence Score Calculation (0-10 scale)
// Clinically reproducible scoring based on study counts
function calculateEvidenceScore(metaAnalyses = 0, rcts = 0, otherStudies = 0) {
  const maPoints = Math.log(1 + metaAnalyses);
  const rctPoints = Math.log(1 + rcts);
  const otherPoints = Math.log(1 + otherStudies);
  
  const raw = 0.4 * maPoints + 0.4 * rctPoints + 0.2 * otherPoints;
  // Normalize: 3.0 ≈ score if MA=10, RCT=50
  return Math.min(10, (raw / 3.0) * 10);
}

// Anti-Inflammatory Score Calculation (0-10 scale)
// Based on documented percentage reductions in inflammatory markers
function calculateAntiInflammatoryScore(reductions = {}) {
  // Convert percentage reductions to 0-10 subscores
  // 0% = 0, ≥40% = 10, linear in between
  const toSubscore = (percent) => Math.min(10, Math.max(0, (percent / 40) * 10));
  
  const weights = {
    esr: 0.35,    // Global marker, cheap, frequently reported
    crp: 0.25,    // Acute-phase, widely cited
    il6: 0.15,    // Upstream cytokine
    tnfa: 0.15,   // Cytokine, joint diseases
    nfkb: 0.10    // Master switch
  };
  
  let totalScore = 0;
  totalScore += weights.esr * toSubscore(reductions.esr || 0);
  totalScore += weights.crp * toSubscore(reductions.crp || 0);
  totalScore += weights.il6 * toSubscore(reductions.il6 || 0);
  totalScore += weights.tnfa * toSubscore(reductions.tnfa || 0);
  totalScore += weights.nfkb * toSubscore(reductions.nfkb || 0);
  
  return Math.round(totalScore * 10) / 10; // Round to 1 decimal
}

const foods = [
  // FATTY FISH (10 foods)
  {
    id: 'salmon',
    name: 'Wild-Caught Salmon',
    category: 'Fatty Fish',
    evidence: { score: 9.4, studies: 234 },
    antiInflammatoryScore: 9.2,
    servingSize: '3.5 oz (100g)',
    frequency: '2-3 times per week',
    keyCompounds: ['EPA', 'DHA', 'Astaxanthin', 'Vitamin D'],
    cost: '$12-20/lb',
    preparation: 'Grilled, baked, or poached - avoid frying',
    description: 'Premier source of anti-inflammatory omega-3 fatty acids with EPA and DHA',
    mechanisms: ['Omega-3 conversion to resolvins', 'COX-2 inhibition', 'NF-κB suppression'],
    sideEffects: 'Choose wild-caught to minimize contaminants',
    interactions: []
  },
  {
    id: 'sardines',
    name: 'Sardines',
    category: 'Fatty Fish',
    evidence: { score: 8.9, studies: 89 },
    antiInflammatoryScore: 9.0,
    servingSize: '3.5 oz (100g)',
    frequency: '2-3 times per week',
    keyCompounds: ['EPA', 'DHA', 'Coenzyme Q10', 'Calcium'],
    cost: '$2-4/can',
    preparation: 'Fresh grilled or canned in water/olive oil',
    description: 'Sustainable, affordable omega-3 powerhouse with low mercury content',
    mechanisms: ['Omega-3 pathway activation', 'Antioxidant protection'],
    sideEffects: 'Very safe, low mercury due to small size',
    interactions: []
  },
  {
    id: 'mackerel',
    name: 'Atlantic Mackerel',
    category: 'Fatty Fish',
    evidence: { score: 8.7, studies: 76 },
    antiInflammatoryScore: 8.8,
    servingSize: '3.5 oz (100g)',
    frequency: '2-3 times per week',
    keyCompounds: ['EPA', 'DHA', 'Vitamin B12', 'Selenium'],
    cost: '$8-12/lb',
    preparation: 'Grilled, smoked, or baked with herbs',
    description: 'Rich omega-3 fish with exceptional vitamin B12 content',
    mechanisms: ['Inflammatory cytokine reduction', 'Omega-3 metabolism'],
    sideEffects: 'Choose Atlantic over King mackerel for lower mercury',
    interactions: []
  },
  {
    id: 'herring',
    name: 'Herring',
    category: 'Fatty Fish',
    evidence: { score: 8.5, studies: 62 },
    antiInflammatoryScore: 8.6,
    servingSize: '3.5 oz (100g)',
    frequency: '2-3 times per week',
    keyCompounds: ['EPA', 'DHA', 'Vitamin D', 'Selenium'],
    cost: '$6-10/lb',
    preparation: 'Pickled, smoked, or fresh grilled',
    description: 'Traditional omega-3 rich fish with high vitamin D content',
    mechanisms: ['Resolution of inflammation', 'Immune modulation'],
    sideEffects: 'Watch sodium in pickled preparations',
    interactions: []
  },
  {
    id: 'anchovies',
    name: 'Anchovies',
    category: 'Fatty Fish',
    evidence: { score: 8.3, studies: 45 },
    antiInflammatoryScore: 8.4,
    servingSize: '2 oz (56g)',
    frequency: '2-3 times per week',
    keyCompounds: ['EPA', 'DHA', 'Calcium', 'Iron'],
    cost: '$4-8/jar',
    preparation: 'Fresh, canned, or as paste in cooking',
    description: 'Small fish with concentrated omega-3s and umami flavor',
    mechanisms: ['Omega-3 anti-inflammatory pathways', 'Calcium signaling'],
    sideEffects: 'High sodium in preserved forms',
    interactions: []
  },
  {
    id: 'rainbow_trout',
    name: 'Rainbow Trout',
    category: 'Fatty Fish',
    evidence: { score: 8.4, studies: 58 },
    antiInflammatoryScore: 8.2,
    servingSize: '3.5 oz (100g)',
    frequency: '2-3 times per week',
    keyCompounds: ['EPA', 'DHA', 'Vitamin B6', 'Potassium'],
    cost: '$10-14/lb',
    preparation: 'Pan-seared, baked, or grilled with lemon',
    description: 'Freshwater fish with balanced omega-3 profile',
    mechanisms: ['Eicosanoid metabolism', 'Inflammatory marker reduction'],
    sideEffects: 'Very safe, often farm-raised sustainably',
    interactions: []
  },
  {
    id: 'arctic_char',
    name: 'Arctic Char',
    category: 'Fatty Fish',
    evidence: { score: 8.2, studies: 42 },
    antiInflammatoryScore: 8.3,
    servingSize: '3.5 oz (100g)',
    frequency: '2-3 times per week',
    keyCompounds: ['EPA', 'DHA', 'Astaxanthin', 'Vitamin E'],
    cost: '$14-18/lb',
    preparation: 'Similar to salmon - grilled or roasted',
    description: 'Sustainable cold-water fish between salmon and trout',
    mechanisms: ['Omega-3 conversion', 'Antioxidant protection'],
    sideEffects: 'Very safe, sustainably farmed',
    interactions: []
  },
  {
    id: 'halibut',
    name: 'Pacific Halibut',
    category: 'Fatty Fish',
    evidence: { score: 7.8, studies: 38 },
    antiInflammatoryScore: 7.5,
    servingSize: '3.5 oz (100g)',
    frequency: '1-2 times per week',
    keyCompounds: ['EPA', 'DHA', 'Magnesium', 'Selenium'],
    cost: '$20-30/lb',
    preparation: 'Baked, grilled, or pan-seared - mild flavor',
    description: 'Lean white fish with moderate omega-3 content',
    mechanisms: ['Inflammatory pathway modulation', 'Selenium antioxidant activity'],
    sideEffects: 'Safe in moderation, larger fish may have more mercury',
    interactions: []
  },
  {
    id: 'black_cod',
    name: 'Black Cod (Sablefish)',
    category: 'Fatty Fish',
    evidence: { score: 8.6, studies: 48 },
    antiInflammatoryScore: 8.7,
    servingSize: '3.5 oz (100g)',
    frequency: '2-3 times per week',
    keyCompounds: ['EPA', 'DHA', 'Vitamin B12', 'Phosphorus'],
    cost: '$25-35/lb',
    preparation: 'Miso-glazed, broiled, or smoked',
    description: 'Buttery fish with exceptionally high omega-3 content',
    mechanisms: ['High omega-3 delivery', 'Inflammation resolution'],
    sideEffects: 'Very safe, sustainable when properly sourced',
    interactions: []
  },
  {
    id: 'oysters',
    name: 'Oysters',
    category: 'Fatty Fish',
    evidence: { score: 8.0, studies: 52 },
    antiInflammatoryScore: 7.8,
    servingSize: '6 medium oysters (84g)',
    frequency: '1-2 times per week',
    keyCompounds: ['EPA', 'DHA', 'Zinc', 'Vitamin B12'],
    cost: '$2-4/oyster',
    preparation: 'Raw, grilled, or baked - avoid fried',
    description: 'Shellfish with exceptional zinc and B12 content',
    mechanisms: ['Zinc immune modulation', 'Omega-3 pathways'],
    sideEffects: 'Raw consumption carries risk - ensure proper sourcing',
    interactions: []
  },

  // BERRIES (10 foods)
  {
    id: 'blueberries',
    name: 'Blueberries',
    category: 'Berries',
    evidence: { score: 8.7, studies: 156 },
    antiInflammatoryScore: 8.5,
    servingSize: '1 cup (148g)',
    frequency: 'Daily',
    keyCompounds: ['Anthocyanins', 'Quercetin', 'Resveratrol', 'Vitamin C'],
    cost: '$3-6/cup',
    preparation: 'Fresh, frozen, or dried - avoid added sugars',
    description: 'Antioxidant-rich brain and heart protective berry',
    mechanisms: ['NF-κB inhibition', 'Antioxidant enzyme activation', 'Endothelial protection'],
    sideEffects: 'Very safe, may interact with blood thinners in very large amounts',
    interactions: []
  },
  {
    id: 'tart_cherries',
    name: 'Tart Cherries',
    category: 'Berries',
    evidence: { score: 8.2, studies: 67 },
    antiInflammatoryScore: 8.8,
    servingSize: '1 cup or 8 oz juice',
    frequency: 'Daily, especially post-exercise',
    keyCompounds: ['Anthocyanins', 'Melatonin', 'Phenolic acids'],
    cost: '$4-8/cup or juice',
    preparation: 'Fresh, juice, or dried - monitor sugar in juice',
    description: 'Natural anti-inflammatory for exercise recovery and sleep',
    mechanisms: ['COX-1 and COX-2 inhibition', 'Uric acid reduction', 'Sleep enhancement'],
    sideEffects: 'Safe, may enhance sleep quality',
    interactions: []
  },
  {
    id: 'strawberries',
    name: 'Strawberries',
    category: 'Berries',
    evidence: { score: 8.3, studies: 98 },
    antiInflammatoryScore: 8.0,
    servingSize: '1 cup sliced (166g)',
    frequency: 'Daily',
    keyCompounds: ['Anthocyanins', 'Ellagic acid', 'Vitamin C', 'Folate'],
    cost: '$3-5/pound',
    preparation: 'Fresh or frozen - organic preferred due to pesticides',
    description: 'Vitamin C rich berry with cardiovascular benefits',
    mechanisms: ['CRP reduction', 'Endothelial function improvement'],
    sideEffects: 'Very safe, choose organic when possible',
    interactions: []
  },
  {
    id: 'blackberries',
    name: 'Blackberries',
    category: 'Berries',
    evidence: { score: 8.1, studies: 72 },
    antiInflammatoryScore: 8.2,
    servingSize: '1 cup (144g)',
    frequency: 'Daily',
    keyCompounds: ['Anthocyanins', 'Ellagic acid', 'Vitamin K', 'Manganese'],
    cost: '$4-6/cup',
    preparation: 'Fresh or frozen - excellent in smoothies',
    description: 'High fiber berry with potent antioxidants',
    mechanisms: ['Oxidative stress reduction', 'Gut microbiome support'],
    sideEffects: 'Very safe, high in seeds and fiber',
    interactions: []
  },
  {
    id: 'raspberries',
    name: 'Raspberries',
    category: 'Berries',
    evidence: { score: 8.0, studies: 64 },
    antiInflammatoryScore: 7.9,
    servingSize: '1 cup (123g)',
    frequency: 'Daily',
    keyCompounds: ['Ellagic acid', 'Quercetin', 'Vitamin C', 'Fiber'],
    cost: '$4-7/cup',
    preparation: 'Fresh or frozen - delicate, eat quickly when fresh',
    description: 'Delicate berry with high fiber and ellagic acid',
    mechanisms: ['Metabolic inflammation reduction', 'Insulin sensitivity'],
    sideEffects: 'Very safe, high in seeds',
    interactions: []
  },
  {
    id: 'cranberries',
    name: 'Cranberries',
    category: 'Berries',
    evidence: { score: 7.8, studies: 86 },
    antiInflammatoryScore: 7.5,
    servingSize: '1/2 cup fresh (55g)',
    frequency: 'Several times per week',
    keyCompounds: ['Proanthocyanidins', 'Quercetin', 'Vitamin C'],
    cost: '$3-5/bag',
    preparation: 'Fresh, dried unsweetened, or pure juice',
    description: 'Tart berry known for urinary tract health',
    mechanisms: ['Bacterial adhesion prevention', 'Oxidative stress reduction'],
    sideEffects: 'Safe, avoid sweetened versions',
    interactions: []
  },
  {
    id: 'goji_berries',
    name: 'Goji Berries',
    category: 'Berries',
    evidence: { score: 7.6, studies: 42 },
    antiInflammatoryScore: 7.8,
    servingSize: '1/4 cup dried (28g)',
    frequency: 'Several times per week',
    keyCompounds: ['Zeaxanthin', 'Polysaccharides', 'Vitamin A'],
    cost: '$15-25/lb',
    preparation: 'Dried, rehydrated in tea, or fresh if available',
    description: 'Traditional Chinese medicine berry with unique polysaccharides',
    mechanisms: ['Immune modulation', 'Oxidative stress protection'],
    sideEffects: 'May interact with blood thinners',
    interactions: []
  },
  {
    id: 'acai_berries',
    name: 'Acai Berries',
    category: 'Berries',
    evidence: { score: 7.4, studies: 38 },
    antiInflammatoryScore: 7.6,
    servingSize: '100g frozen pulp',
    frequency: 'Several times per week',
    keyCompounds: ['Anthocyanins', 'Flavonoids', 'Omega-9'],
    cost: '$10-15/pack',
    preparation: 'Frozen pulp in smoothie bowls - fresh unavailable outside Brazil',
    description: 'Amazonian berry with exceptional anthocyanin content',
    mechanisms: ['Antioxidant activity', 'Cholesterol modulation'],
    sideEffects: 'Safe, watch added sugars in commercial products',
    interactions: []
  },
  {
    id: 'elderberries',
    name: 'Elderberries',
    category: 'Berries',
    evidence: { score: 7.5, studies: 34 },
    antiInflammatoryScore: 7.7,
    servingSize: '1/2 cup cooked (75g)',
    frequency: 'During cold season',
    keyCompounds: ['Anthocyanins', 'Flavonoids', 'Vitamin C'],
    cost: '$20-30/lb dried',
    preparation: 'Must be cooked - syrup, tea, or supplement',
    description: 'Traditional immune-supporting berry',
    mechanisms: ['Viral replication inhibition', 'Immune modulation'],
    sideEffects: 'Never eat raw - must be cooked',
    interactions: []
  },
  {
    id: 'pomegranate',
    name: 'Pomegranate Seeds',
    category: 'Berries',
    evidence: { score: 8.4, studies: 124 },
    antiInflammatoryScore: 8.3,
    servingSize: '1/2 cup arils (87g)',
    frequency: 'Several times per week',
    keyCompounds: ['Punicalagins', 'Ellagic acid', 'Anthocyanins'],
    cost: '$3-5/fruit',
    preparation: 'Fresh arils or 100% juice - avoid added sugars',
    description: 'Ancient fruit with potent antioxidant compounds',
    mechanisms: ['NF-κB inhibition', 'Oxidative stress reduction'],
    sideEffects: 'Safe, may interact with statins',
    interactions: []
  },

  // LEAFY GREENS (10 foods)
  {
    id: 'spinach',
    name: 'Spinach',
    category: 'Leafy Greens',
    evidence: { score: 8.1, studies: 124 },
    antiInflammatoryScore: 7.8,
    servingSize: '1 cup raw (30g) or 1/2 cup cooked',
    frequency: 'Daily',
    keyCompounds: ['Lutein', 'Zeaxanthin', 'Folate', 'Vitamin K'],
    cost: '$2-4/bunch',
    preparation: 'Raw in salads, sautéed, or steamed',
    description: 'Nutrient-dense green with eye-protective carotenoids',
    mechanisms: ['Carotenoid antioxidant activity', 'Nitric oxide enhancement'],
    sideEffects: 'High in oxalates - vary greens if prone to kidney stones',
    interactions: []
  },
  {
    id: 'kale',
    name: 'Kale',
    category: 'Leafy Greens',
    evidence: { score: 8.3, studies: 89 },
    antiInflammatoryScore: 8.0,
    servingSize: '1 cup chopped (67g)',
    frequency: 'Daily',
    keyCompounds: ['Glucosinolates', 'Vitamin C', 'Beta-carotene', 'Quercetin'],
    cost: '$2-5/bunch',
    preparation: 'Raw massaged, sautéed, or baked as chips',
    description: 'Cruciferous superfood with detoxification support',
    mechanisms: ['Nrf2 pathway activation', 'Detoxification enzyme support'],
    sideEffects: 'Very safe, may affect thyroid if consumed raw in huge amounts',
    interactions: []
  },
  {
    id: 'swiss_chard',
    name: 'Swiss Chard',
    category: 'Leafy Greens',
    evidence: { score: 7.9, studies: 56 },
    antiInflammatoryScore: 7.6,
    servingSize: '1 cup chopped (36g)',
    frequency: 'Daily',
    keyCompounds: ['Betalains', 'Vitamin K', 'Magnesium', 'Potassium'],
    cost: '$2-4/bunch',
    preparation: 'Sautéed with garlic, steamed, or in soups',
    description: 'Colorful green with unique betalain pigments',
    mechanisms: ['Betalain antioxidant activity', 'Mineral balance'],
    sideEffects: 'High in oxalates like spinach',
    interactions: []
  },
  {
    id: 'arugula',
    name: 'Arugula',
    category: 'Leafy Greens',
    evidence: { score: 7.8, studies: 42 },
    antiInflammatoryScore: 7.5,
    servingSize: '1 cup (20g)',
    frequency: 'Daily',
    keyCompounds: ['Glucosinolates', 'Vitamin K', 'Folate', 'Nitrates'],
    cost: '$3-5/bag',
    preparation: 'Raw in salads, wilted on pizza, or pesto',
    description: 'Peppery green with detoxification compounds',
    mechanisms: ['Glucosinolate metabolism', 'Nitric oxide production'],
    sideEffects: 'Very safe, distinctive peppery taste',
    interactions: []
  },
  {
    id: 'collard_greens',
    name: 'Collard Greens',
    category: 'Leafy Greens',
    evidence: { score: 7.9, studies: 48 },
    antiInflammatoryScore: 7.7,
    servingSize: '1 cup chopped (36g)',
    frequency: 'Several times per week',
    keyCompounds: ['Glucosinolates', 'Vitamin K', 'Calcium', 'Vitamin A'],
    cost: '$2-3/bunch',
    preparation: 'Slow-cooked, sautéed, or used as wraps',
    description: 'Southern green with exceptional vitamin K content',
    mechanisms: ['Calcium signaling', 'Detoxification support'],
    sideEffects: 'Very safe, traditional cooking adds sodium',
    interactions: []
  },
  {
    id: 'watercress',
    name: 'Watercress',
    category: 'Leafy Greens',
    evidence: { score: 7.7, studies: 38 },
    antiInflammatoryScore: 7.8,
    servingSize: '1 cup (34g)',
    frequency: 'Several times per week',
    keyCompounds: ['PEITC', 'Vitamin K', 'Vitamin C', 'Beta-carotene'],
    cost: '$3-5/bunch',
    preparation: 'Raw in salads, soups, or sandwiches',
    description: 'Peppery aquatic green with cancer-protective compounds',
    mechanisms: ['PEITC phase II enzyme induction', 'Antioxidant activity'],
    sideEffects: 'Very safe, mild peppery flavor',
    interactions: []
  },
  {
    id: 'romaine_lettuce',
    name: 'Romaine Lettuce',
    category: 'Leafy Greens',
    evidence: { score: 7.2, studies: 34 },
    antiInflammatoryScore: 6.8,
    servingSize: '2 cups chopped (94g)',
    frequency: 'Daily',
    keyCompounds: ['Vitamin K', 'Folate', 'Beta-carotene', 'Vitamin C'],
    cost: '$2-4/head',
    preparation: 'Raw in salads, grilled, or as wraps',
    description: 'Crisp lettuce with good nutrient density',
    mechanisms: ['Folate methylation support', 'Hydration'],
    sideEffects: 'Very safe, ensure proper washing',
    interactions: []
  },
  {
    id: 'dandelion_greens',
    name: 'Dandelion Greens',
    category: 'Leafy Greens',
    evidence: { score: 7.4, studies: 28 },
    antiInflammatoryScore: 7.6,
    servingSize: '1 cup chopped (55g)',
    frequency: 'Several times per week',
    keyCompounds: ['Taraxasterol', 'Vitamin K', 'Potassium', 'Inulin'],
    cost: '$4-6/bunch',
    preparation: 'Sautéed with garlic, in salads when young',
    description: 'Bitter green with liver support properties',
    mechanisms: ['Liver detoxification', 'Prebiotic fiber effects'],
    sideEffects: 'Safe, very bitter - blanch to reduce',
    interactions: []
  },
  {
    id: 'mustard_greens',
    name: 'Mustard Greens',
    category: 'Leafy Greens',
    evidence: { score: 7.5, studies: 32 },
    antiInflammatoryScore: 7.7,
    servingSize: '1 cup chopped (56g)',
    frequency: 'Several times per week',
    keyCompounds: ['Glucosinolates', 'Vitamin K', 'Vitamin A', 'Vitamin C'],
    cost: '$2-3/bunch',
    preparation: 'Sautéed, braised, or pickled',
    description: 'Spicy green with detoxification compounds',
    mechanisms: ['Glucosinolate activation', 'Antioxidant enzyme support'],
    sideEffects: 'Safe, spicy flavor may be intense',
    interactions: []
  },
  {
    id: 'beet_greens',
    name: 'Beet Greens',
    category: 'Leafy Greens',
    evidence: { score: 7.3, studies: 26 },
    antiInflammatoryScore: 7.4,
    servingSize: '1 cup chopped (38g)',
    frequency: 'Several times per week',
    keyCompounds: ['Betalains', 'Vitamin K', 'Potassium', 'Nitrates'],
    cost: '$2-4/bunch with beets',
    preparation: 'Sautéed with garlic or added to soups',
    description: 'Often discarded tops with impressive nutrition',
    mechanisms: ['Nitric oxide production', 'Betalain antioxidant activity'],
    sideEffects: 'High in oxalates',
    interactions: []
  },

  // NUTS AND SEEDS (10 foods)
  {
    id: 'walnuts',
    name: 'Walnuts',
    category: 'Nuts & Seeds',
    evidence: { score: 9.1, studies: 145 },
    antiInflammatoryScore: 8.9,
    servingSize: '1 oz (28g) - about 7 halves',
    frequency: 'Daily',
    keyCompounds: ['ALA omega-3', 'Tocopherols', 'Ellagic acid', 'Melatonin'],
    cost: '$8-12/lb',
    preparation: 'Raw, lightly toasted, or soaked',
    description: 'Only nut with significant omega-3 content',
    mechanisms: ['Alpha-linolenic acid conversion', 'Endothelial function improvement'],
    sideEffects: 'Tree nut allergy concern',
    interactions: []
  },
  {
    id: 'flaxseeds',
    name: 'Ground Flaxseeds',
    category: 'Nuts & Seeds',
    evidence: { score: 8.4, studies: 78 },
    antiInflammatoryScore: 8.2,
    servingSize: '1 tablespoon ground (7g)',
    frequency: 'Daily',
    keyCompounds: ['ALA omega-3', 'Lignans', 'Fiber', 'Protein'],
    cost: '$3-6/lb',
    preparation: 'Must be ground - add to smoothies, yogurt, oatmeal',
    description: 'Plant omega-3 and hormone-balancing lignans',
    mechanisms: ['Omega-3 conversion', 'Phytoestrogen activity', 'Gut microbiome support'],
    sideEffects: 'Start slowly to avoid digestive upset',
    interactions: []
  },
  {
    id: 'chia_seeds',
    name: 'Chia Seeds',
    category: 'Nuts & Seeds',
    evidence: { score: 8.2, studies: 64 },
    antiInflammatoryScore: 8.0,
    servingSize: '1 tablespoon (12g)',
    frequency: 'Daily',
    keyCompounds: ['ALA omega-3', 'Fiber', 'Protein', 'Calcium'],
    cost: '$8-12/lb',
    preparation: 'Soak in liquid, add to puddings, smoothies',
    description: 'Versatile seed with gel-forming properties',
    mechanisms: ['Omega-3 delivery', 'Blood sugar stabilization'],
    sideEffects: 'Very safe, hydrate well',
    interactions: []
  },
  {
    id: 'hemp_seeds',
    name: 'Hemp Hearts',
    category: 'Nuts & Seeds',
    evidence: { score: 7.8, studies: 42 },
    antiInflammatoryScore: 7.9,
    servingSize: '3 tablespoons (30g)',
    frequency: 'Daily',
    keyCompounds: ['GLA', 'ALA', 'Protein', 'Magnesium'],
    cost: '$10-15/lb',
    preparation: 'Sprinkle on salads, yogurt, or blend in smoothies',
    description: 'Complete protein with unique GLA omega-6',
    mechanisms: ['GLA anti-inflammatory conversion', 'Protein synthesis'],
    sideEffects: 'Very safe, mild nutty flavor',
    interactions: []
  },
  {
    id: 'almonds',
    name: 'Almonds',
    category: 'Nuts & Seeds',
    evidence: { score: 8.5, studies: 98 },
    antiInflammatoryScore: 7.8,
    servingSize: '1 oz (28g) - about 23 almonds',
    frequency: 'Daily',
    keyCompounds: ['Vitamin E', 'Magnesium', 'Fiber', 'Protein'],
    cost: '$6-10/lb',
    preparation: 'Raw, roasted, or as almond butter',
    description: 'Vitamin E rich nut with heart benefits',
    mechanisms: ['Vitamin E antioxidant activity', 'LDL oxidation prevention'],
    sideEffects: 'Tree nut allergy concern',
    interactions: []
  },
  {
    id: 'brazil_nuts',
    name: 'Brazil Nuts',
    category: 'Nuts & Seeds',
    evidence: { score: 7.6, studies: 38 },
    antiInflammatoryScore: 7.5,
    servingSize: '1-2 nuts (5-10g)',
    frequency: '2-3 times per week',
    keyCompounds: ['Selenium', 'Magnesium', 'Vitamin E', 'Zinc'],
    cost: '$10-15/lb',
    preparation: 'Raw or lightly roasted - limit intake',
    description: 'Exceptional selenium source - eat in moderation',
    mechanisms: ['Selenium antioxidant enzyme support', 'Thyroid function'],
    sideEffects: 'Limit to 1-2 nuts daily to avoid selenium toxicity',
    interactions: []
  },
  {
    id: 'pecans',
    name: 'Pecans',
    category: 'Nuts & Seeds',
    evidence: { score: 7.9, studies: 52 },
    antiInflammatoryScore: 7.6,
    servingSize: '1 oz (28g) - about 19 halves',
    frequency: 'Several times per week',
    keyCompounds: ['Tocopherols', 'Phenolic acids', 'Manganese'],
    cost: '$12-18/lb',
    preparation: 'Raw, toasted, or in dishes',
    description: 'Antioxidant-rich nut with buttery flavor',
    mechanisms: ['Antioxidant activity', 'LDL cholesterol reduction'],
    sideEffects: 'Tree nut allergy concern',
    interactions: []
  },
  {
    id: 'pumpkin_seeds',
    name: 'Pumpkin Seeds',
    category: 'Nuts & Seeds',
    evidence: { score: 7.7, studies: 46 },
    antiInflammatoryScore: 7.4,
    servingSize: '1 oz (28g)',
    frequency: 'Several times per week',
    keyCompounds: ['Magnesium', 'Zinc', 'Iron', 'Phytosterols'],
    cost: '$8-12/lb',
    preparation: 'Raw, roasted, or ground into butter',
    description: 'Mineral-rich seed with prostate health benefits',
    mechanisms: ['Mineral cofactor support', 'Phytosterol activity'],
    sideEffects: 'Very safe',
    interactions: []
  },
  {
    id: 'sunflower_seeds',
    name: 'Sunflower Seeds',
    category: 'Nuts & Seeds',
    evidence: { score: 7.5, studies: 42 },
    antiInflammatoryScore: 7.2,
    servingSize: '1 oz (28g)',
    frequency: 'Several times per week',
    keyCompounds: ['Vitamin E', 'Selenium', 'Magnesium', 'Phytosterols'],
    cost: '$3-6/lb',
    preparation: 'Raw or roasted, unsalted preferred',
    description: 'Affordable seed with high vitamin E',
    mechanisms: ['Vitamin E antioxidant protection', 'Cholesterol modulation'],
    sideEffects: 'Watch sodium in salted versions',
    interactions: []
  },
  {
    id: 'sesame_seeds',
    name: 'Sesame Seeds/Tahini',
    category: 'Nuts & Seeds',
    evidence: { score: 7.6, studies: 48 },
    antiInflammatoryScore: 7.3,
    servingSize: '1 tablespoon (9g)',
    frequency: 'Several times per week',
    keyCompounds: ['Sesamin', 'Sesamolin', 'Calcium', 'Magnesium'],
    cost: '$6-10/lb',
    preparation: 'Whole seeds, tahini paste, or oil',
    description: 'Calcium-rich seed with unique lignans',
    mechanisms: ['Lignan antioxidant activity', 'Cholesterol metabolism'],
    sideEffects: 'Possible allergen for some',
    interactions: []
  },

  // SPICES AND HERBS (15 foods)
  {
    id: 'turmeric',
    name: 'Turmeric (Fresh Root)',
    category: 'Spices & Herbs',
    evidence: { score: 9.2, studies: 267 },
    antiInflammatoryScore: 9.5,
    servingSize: '1 teaspoon grated (2g)',
    frequency: 'Daily',
    keyCompounds: ['Curcumin', 'Turmerones', 'Volatile oils'],
    cost: '$3-5/root',
    preparation: 'Grate fresh, combine with black pepper and fat',
    description: 'Golden root with potent curcumin compounds',
    mechanisms: ['NF-κB inhibition', 'COX-2 suppression', 'Antioxidant enzyme activation'],
    sideEffects: 'May enhance blood thinning medications',
    interactions: []
  },
  {
    id: 'ginger',
    name: 'Ginger (Fresh Root)',
    category: 'Spices & Herbs',
    evidence: { score: 8.6, studies: 134 },
    antiInflammatoryScore: 8.8,
    servingSize: '1 teaspoon grated (2g)',
    frequency: 'Daily',
    keyCompounds: ['Gingerols', 'Shogaols', 'Zingerone'],
    cost: '$2-4/root',
    preparation: 'Fresh grated, tea, or cooked',
    description: 'Warming spice with powerful anti-inflammatory effects',
    mechanisms: ['COX-2 inhibition', 'Prostaglandin suppression', 'Nausea reduction'],
    sideEffects: 'May enhance blood thinning effects',
    interactions: []
  },
  {
    id: 'garlic',
    name: 'Garlic',
    category: 'Spices & Herbs',
    evidence: { score: 8.4, studies: 156 },
    antiInflammatoryScore: 8.2,
    servingSize: '1-2 cloves (3-6g)',
    frequency: 'Daily',
    keyCompounds: ['Allicin', 'S-allyl cysteine', 'Diallyl sulfides'],
    cost: '$2-4/lb',
    preparation: 'Crush and wait 10 minutes before cooking',
    description: 'Pungent bulb with cardiovascular benefits',
    mechanisms: ['Sulfur compound activation', 'NF-κB modulation'],
    sideEffects: 'May cause GI upset in some, blood thinning',
    interactions: []
  },
  {
    id: 'cinnamon',
    name: 'Ceylon Cinnamon',
    category: 'Spices & Herbs',
    evidence: { score: 8.0, studies: 89 },
    antiInflammatoryScore: 7.8,
    servingSize: '1/2 teaspoon (1.3g)',
    frequency: 'Daily',
    keyCompounds: ['Cinnamaldehyde', 'Procyanidins', 'Eugenol'],
    cost: '$15-25/lb',
    preparation: 'Ground in foods, tea, or smoothies',
    description: 'Sweet spice with blood sugar benefits',
    mechanisms: ['Insulin sensitivity improvement', 'Antioxidant activity'],
    sideEffects: 'Choose Ceylon over Cassia to avoid coumarin',
    interactions: []
  },
  {
    id: 'black_pepper',
    name: 'Black Pepper',
    category: 'Spices & Herbs',
    evidence: { score: 7.6, studies: 62 },
    antiInflammatoryScore: 7.4,
    servingSize: '1/4 teaspoon (0.5g)',
    frequency: 'Daily',
    keyCompounds: ['Piperine', 'Beta-caryophyllene', 'Limonene'],
    cost: '$10-15/lb',
    preparation: 'Freshly ground on foods',
    description: 'Enhances absorption of other compounds',
    mechanisms: ['Bioavailability enhancement', 'Anti-inflammatory signaling'],
    sideEffects: 'Very safe, enhances turmeric absorption',
    interactions: []
  },
  {
    id: 'cayenne',
    name: 'Cayenne Pepper',
    category: 'Spices & Herbs',
    evidence: { score: 7.8, studies: 58 },
    antiInflammatoryScore: 8.0,
    servingSize: '1/4 teaspoon (0.45g)',
    frequency: 'Several times per week',
    keyCompounds: ['Capsaicin', 'Vitamin C', 'Beta-carotene'],
    cost: '$8-12/lb',
    preparation: 'Add to dishes, drinks, or topically',
    description: 'Hot pepper with metabolism benefits',
    mechanisms: ['TRPV1 receptor activation', 'Substance P depletion'],
    sideEffects: 'Start with small amounts, may cause GI upset',
    interactions: []
  },
  {
    id: 'rosemary',
    name: 'Fresh Rosemary',
    category: 'Spices & Herbs',
    evidence: { score: 7.9, studies: 54 },
    antiInflammatoryScore: 7.7,
    servingSize: '1 teaspoon fresh (0.7g)',
    frequency: 'Several times per week',
    keyCompounds: ['Rosmarinic acid', 'Carnosic acid', 'Camphor'],
    cost: '$3-5/bunch',
    preparation: 'Fresh or dried in cooking, tea',
    description: 'Aromatic herb with cognitive benefits',
    mechanisms: ['Antioxidant enzyme activation', 'COX-2 inhibition'],
    sideEffects: 'Very safe in culinary amounts',
    interactions: []
  },
  {
    id: 'oregano',
    name: 'Oregano',
    category: 'Spices & Herbs',
    evidence: { score: 7.7, studies: 46 },
    antiInflammatoryScore: 7.8,
    servingSize: '1 teaspoon dried (1g)',
    frequency: 'Several times per week',
    keyCompounds: ['Carvacrol', 'Thymol', 'Rosmarinic acid'],
    cost: '$15-20/lb',
    preparation: 'Fresh or dried in cooking',
    description: 'Mediterranean herb with antimicrobial properties',
    mechanisms: ['Antimicrobial activity', 'Antioxidant protection'],
    sideEffects: 'Very safe in culinary amounts',
    interactions: []
  },
  {
    id: 'thyme',
    name: 'Thyme',
    category: 'Spices & Herbs',
    evidence: { score: 7.5, studies: 42 },
    antiInflammatoryScore: 7.6,
    servingSize: '1 teaspoon fresh (0.8g)',
    frequency: 'Several times per week',
    keyCompounds: ['Thymol', 'Carvacrol', 'Linalool'],
    cost: '$3-5/bunch fresh',
    preparation: 'Fresh or dried in cooking, tea',
    description: 'Aromatic herb with respiratory benefits',
    mechanisms: ['Antimicrobial activity', 'Bronchodilation'],
    sideEffects: 'Very safe in culinary amounts',
    interactions: []
  },
  {
    id: 'basil',
    name: 'Fresh Basil',
    category: 'Spices & Herbs',
    evidence: { score: 7.4, studies: 38 },
    antiInflammatoryScore: 7.3,
    servingSize: '2 tablespoons fresh (5g)',
    frequency: 'Several times per week',
    keyCompounds: ['Eugenol', 'Linalool', 'Citronellol'],
    cost: '$3-5/bunch',
    preparation: 'Fresh in salads, pesto, or cooking',
    description: 'Aromatic herb with stress-reducing properties',
    mechanisms: ['COX enzyme inhibition', 'Adaptogenic effects'],
    sideEffects: 'Very safe',
    interactions: []
  },
  {
    id: 'cloves',
    name: 'Cloves',
    category: 'Spices & Herbs',
    evidence: { score: 7.8, studies: 44 },
    antiInflammatoryScore: 8.1,
    servingSize: '1/4 teaspoon ground (0.5g)',
    frequency: 'Several times per week',
    keyCompounds: ['Eugenol', 'Beta-caryophyllene', 'Gallic acid'],
    cost: '$20-30/lb',
    preparation: 'Ground in baking, whole in cooking',
    description: 'Intensely aromatic spice with dental benefits',
    mechanisms: ['Eugenol anti-inflammatory activity', 'Antimicrobial effects'],
    sideEffects: 'Use sparingly - very potent',
    interactions: []
  },
  {
    id: 'cardamom',
    name: 'Cardamom',
    category: 'Spices & Herbs',
    evidence: { score: 7.3, studies: 32 },
    antiInflammatoryScore: 7.2,
    servingSize: '1/4 teaspoon ground (0.5g)',
    frequency: 'Several times per week',
    keyCompounds: ['1,8-cineole', 'Alpha-terpineol', 'Limonene'],
    cost: '$40-60/lb',
    preparation: 'Pods or ground in tea, coffee, dishes',
    description: 'Aromatic spice with digestive benefits',
    mechanisms: ['Digestive enzyme stimulation', 'Antioxidant activity'],
    sideEffects: 'Very safe',
    interactions: []
  },
  {
    id: 'saffron',
    name: 'Saffron',
    category: 'Spices & Herbs',
    evidence: { score: 7.9, studies: 48 },
    antiInflammatoryScore: 7.8,
    servingSize: 'Pinch (0.1g)',
    frequency: 'Weekly',
    keyCompounds: ['Crocin', 'Safranal', 'Picrocrocin'],
    cost: '$500-1000/lb',
    preparation: 'Steep in warm liquid before using',
    description: 'Precious spice with mood benefits',
    mechanisms: ['Serotonin modulation', 'Antioxidant activity'],
    sideEffects: 'Very safe in culinary amounts',
    interactions: []
  },
  {
    id: 'sage',
    name: 'Sage',
    category: 'Spices & Herbs',
    evidence: { score: 7.4, studies: 36 },
    antiInflammatoryScore: 7.5,
    servingSize: '1 teaspoon fresh (0.7g)',
    frequency: 'Several times per week',
    keyCompounds: ['Rosmarinic acid', 'Carnosic acid', 'Ursolic acid'],
    cost: '$3-5/bunch',
    preparation: 'Fresh or dried in cooking, tea',
    description: 'Cognitive-supporting herb',
    mechanisms: ['Acetylcholinesterase inhibition', 'Antioxidant activity'],
    sideEffects: 'Very safe in culinary amounts',
    interactions: []
  },
  {
    id: 'fennel_seeds',
    name: 'Fennel Seeds',
    category: 'Spices & Herbs',
    evidence: { score: 7.2, studies: 28 },
    antiInflammatoryScore: 7.1,
    servingSize: '1 teaspoon (2g)',
    frequency: 'Several times per week',
    keyCompounds: ['Anethole', 'Fenchone', 'Estragole'],
    cost: '$8-12/lb',
    preparation: 'Whole or ground, tea after meals',
    description: 'Digestive aid with licorice-like flavor',
    mechanisms: ['Smooth muscle relaxation', 'Gas reduction'],
    sideEffects: 'Very safe',
    interactions: []
  },

  // HEALTHY FATS (10 foods)
  {
    id: 'olive_oil',
    name: 'Extra Virgin Olive Oil',
    category: 'Healthy Fats',
    evidence: { score: 9.3, studies: 198 },
    antiInflammatoryScore: 8.7,
    servingSize: '1 tablespoon (14g)',
    frequency: 'Daily',
    keyCompounds: ['Oleic acid', 'Oleocanthal', 'Polyphenols', 'Vitamin E'],
    cost: '$8-20/bottle',
    preparation: 'Cold use preferred, low-heat cooking',
    description: 'Mediterranean staple with natural COX inhibitor',
    mechanisms: ['Oleocanthal COX inhibition', 'Monounsaturated fat benefits'],
    sideEffects: 'Very safe, choose authentic EVOO',
    interactions: []
  },
  {
    id: 'avocado',
    name: 'Avocado',
    category: 'Healthy Fats',
    evidence: { score: 8.2, studies: 89 },
    antiInflammatoryScore: 7.9,
    servingSize: '1/2 medium (100g)',
    frequency: 'Daily',
    keyCompounds: ['Oleic acid', 'Lutein', 'Glutathione', 'Fiber'],
    cost: '$1-3/avocado',
    preparation: 'Raw in salads, guacamole, smoothies',
    description: 'Creamy fruit enhancing nutrient absorption',
    mechanisms: ['Monounsaturated fat benefits', 'Carotenoid absorption enhancement'],
    sideEffects: 'High calorie density',
    interactions: []
  },
  {
    id: 'coconut_oil',
    name: 'Virgin Coconut Oil',
    category: 'Healthy Fats',
    evidence: { score: 7.2, studies: 64 },
    antiInflammatoryScore: 6.8,
    servingSize: '1 tablespoon (14g)',
    frequency: 'Several times per week',
    keyCompounds: ['Lauric acid', 'MCTs', 'Vitamin E'],
    cost: '$10-15/jar',
    preparation: 'Cooking, baking, or in coffee',
    description: 'MCT-rich oil with antimicrobial properties',
    mechanisms: ['MCT metabolism', 'Lauric acid antimicrobial activity'],
    sideEffects: 'High in saturated fat - use moderately',
    interactions: []
  },
  {
    id: 'avocado_oil',
    name: 'Avocado Oil',
    category: 'Healthy Fats',
    evidence: { score: 7.8, studies: 42 },
    antiInflammatoryScore: 7.5,
    servingSize: '1 tablespoon (14g)',
    frequency: 'Several times per week',
    keyCompounds: ['Oleic acid', 'Lutein', 'Vitamin E'],
    cost: '$10-15/bottle',
    preparation: 'High-heat cooking, dressings',
    description: 'High smoke point oil with similar benefits to avocados',
    mechanisms: ['Monounsaturated fat benefits', 'Heat-stable cooking oil'],
    sideEffects: 'Very safe',
    interactions: []
  },
  {
    id: 'mct_oil',
    name: 'MCT Oil',
    category: 'Healthy Fats',
    evidence: { score: 7.0, studies: 38 },
    antiInflammatoryScore: 6.5,
    servingSize: '1 tablespoon (14g)',
    frequency: 'Several times per week',
    keyCompounds: ['Caprylic acid', 'Capric acid'],
    cost: '$20-30/bottle',
    preparation: 'In coffee, smoothies, or dressings',
    description: 'Rapidly absorbed fats for quick energy',
    mechanisms: ['Ketone production', 'Rapid energy metabolism'],
    sideEffects: 'Start slowly to avoid GI upset',
    interactions: []
  },
  {
    id: 'ghee',
    name: 'Grass-Fed Ghee',
    category: 'Healthy Fats',
    evidence: { score: 7.1, studies: 34 },
    antiInflammatoryScore: 6.9,
    servingSize: '1 teaspoon (5g)',
    frequency: 'Several times per week',
    keyCompounds: ['Butyric acid', 'CLA', 'Vitamin K2'],
    cost: '$15-25/jar',
    preparation: 'High-heat cooking, spreading',
    description: 'Clarified butter with gut health benefits',
    mechanisms: ['Butyric acid gut support', 'Fat-soluble vitamin delivery'],
    sideEffects: 'Dairy-derived but lactose-free',
    interactions: []
  },
  {
    id: 'walnut_oil',
    name: 'Walnut Oil',
    category: 'Healthy Fats',
    evidence: { score: 7.6, studies: 28 },
    antiInflammatoryScore: 7.8,
    servingSize: '1 tablespoon (14g)',
    frequency: 'Several times per week',
    keyCompounds: ['ALA omega-3', 'Tocopherols', 'Phytosterols'],
    cost: '$15-20/bottle',
    preparation: 'Cold use only - salad dressings',
    description: 'Omega-3 rich finishing oil',
    mechanisms: ['ALA omega-3 delivery', 'Antioxidant activity'],
    sideEffects: 'Never heat - oxidizes easily',
    interactions: []
  },
  {
    id: 'flaxseed_oil',
    name: 'Flaxseed Oil',
    category: 'Healthy Fats',
    evidence: { score: 7.8, studies: 52 },
    antiInflammatoryScore: 7.9,
    servingSize: '1 tablespoon (14g)',
    frequency: 'Daily',
    keyCompounds: ['ALA omega-3', 'Lignans'],
    cost: '$10-15/bottle',
    preparation: 'Cold use only - refrigerate after opening',
    description: 'Concentrated plant omega-3 source',
    mechanisms: ['ALA conversion to EPA/DHA', 'Lignan phytoestrogen activity'],
    sideEffects: 'Never heat, keep refrigerated',
    interactions: []
  },
  {
    id: 'hemp_oil',
    name: 'Hemp Seed Oil',
    category: 'Healthy Fats',
    evidence: { score: 7.3, studies: 32 },
    antiInflammatoryScore: 7.4,
    servingSize: '1 tablespoon (14g)',
    frequency: 'Several times per week',
    keyCompounds: ['GLA', 'ALA', 'SDA'],
    cost: '$12-18/bottle',
    preparation: 'Cold use - dressings and smoothies',
    description: 'Balanced omega-3 and omega-6 ratio',
    mechanisms: ['GLA anti-inflammatory conversion', 'Omega balance'],
    sideEffects: 'Keep refrigerated',
    interactions: []
  },
  {
    id: 'sesame_oil',
    name: 'Sesame Oil',
    category: 'Healthy Fats',
    evidence: { score: 7.2, studies: 38 },
    antiInflammatoryScore: 7.0,
    servingSize: '1 tablespoon (14g)',
    frequency: 'Several times per week',
    keyCompounds: ['Sesamin', 'Sesamolin', 'Vitamin E'],
    cost: '$8-12/bottle',
    preparation: 'Cooking or finishing oil',
    description: 'Flavorful oil with unique antioxidants',
    mechanisms: ['Lignan antioxidant activity', 'Cholesterol modulation'],
    sideEffects: 'Possible allergen',
    interactions: []
  },

  // BEVERAGES (10 foods)
  {
    id: 'green_tea',
    name: 'Green Tea',
    category: 'Beverages',
    evidence: { score: 8.8, studies: 245 },
    antiInflammatoryScore: 8.3,
    servingSize: '1 cup (240ml)',
    frequency: '2-3 cups daily',
    keyCompounds: ['EGCG', 'Catechins', 'L-theanine', 'Caffeine'],
    cost: '$10-30/lb tea',
    preparation: 'Steep 2-3 min at 160-180°F',
    description: 'Ancient beverage with modern science backing',
    mechanisms: ['EGCG polyphenol activity', 'NF-κB inhibition', 'Stress reduction'],
    sideEffects: 'Moderate caffeine (25-50mg)',
    interactions: []
  },
  {
    id: 'matcha',
    name: 'Matcha Green Tea',
    category: 'Beverages',
    evidence: { score: 8.6, studies: 68 },
    antiInflammatoryScore: 8.5,
    servingSize: '1 teaspoon (2g)',
    frequency: '1-2 servings daily',
    keyCompounds: ['EGCG', 'L-theanine', 'Chlorophyll', 'Caffeine'],
    cost: '$20-50/oz',
    preparation: 'Whisk in hot water, add to smoothies',
    description: 'Concentrated green tea powder',
    mechanisms: ['Enhanced EGCG delivery', 'Sustained energy release'],
    sideEffects: 'Higher caffeine (70mg)',
    interactions: []
  },
  {
    id: 'white_tea',
    name: 'White Tea',
    category: 'Beverages',
    evidence: { score: 7.8, studies: 42 },
    antiInflammatoryScore: 7.6,
    servingSize: '1 cup (240ml)',
    frequency: '2-3 cups daily',
    keyCompounds: ['Catechins', 'Polyphenols', 'L-theanine'],
    cost: '$15-40/lb',
    preparation: 'Steep 4-6 min at 175-185°F',
    description: 'Delicate tea with high antioxidants',
    mechanisms: ['Polyphenol antioxidant activity', 'Gentle stimulation'],
    sideEffects: 'Low caffeine (15-30mg)',
    interactions: []
  },
  {
    id: 'oolong_tea',
    name: 'Oolong Tea',
    category: 'Beverages',
    evidence: { score: 7.9, studies: 56 },
    antiInflammatoryScore: 7.7,
    servingSize: '1 cup (240ml)',
    frequency: '2-3 cups daily',
    keyCompounds: ['Theaflavins', 'EGCG', 'Theanine'],
    cost: '$20-50/lb',
    preparation: 'Steep 1-3 min at 185-205°F',
    description: 'Semi-fermented tea with unique profile',
    mechanisms: ['Metabolism support', 'Polyphenol activity'],
    sideEffects: 'Moderate caffeine (30-50mg)',
    interactions: []
  },
  {
    id: 'hibiscus_tea',
    name: 'Hibiscus Tea',
    category: 'Beverages',
    evidence: { score: 7.6, studies: 48 },
    antiInflammatoryScore: 7.5,
    servingSize: '1 cup (240ml)',
    frequency: '1-2 cups daily',
    keyCompounds: ['Anthocyanins', 'Vitamin C', 'Organic acids'],
    cost: '$10-15/lb',
    preparation: 'Steep 5-10 min in boiling water',
    description: 'Tart tea with blood pressure benefits',
    mechanisms: ['ACE inhibition', 'Diuretic effects'],
    sideEffects: 'May lower blood pressure',
    interactions: []
  },
  {
    id: 'chamomile_tea',
    name: 'Chamomile Tea',
    category: 'Beverages',
    evidence: { score: 7.3, studies: 38 },
    antiInflammatoryScore: 7.0,
    servingSize: '1 cup (240ml)',
    frequency: '1-2 cups daily',
    keyCompounds: ['Apigenin', 'Bisabolol', 'Chamazulene'],
    cost: '$8-12/lb',
    preparation: 'Steep 5 min in boiling water',
    description: 'Calming tea with sleep benefits',
    mechanisms: ['GABA receptor modulation', 'Anti-anxiety effects'],
    sideEffects: 'Avoid if allergic to ragweed',
    interactions: []
  },
  {
    id: 'rooibos_tea',
    name: 'Rooibos Tea',
    category: 'Beverages',
    evidence: { score: 7.2, studies: 32 },
    antiInflammatoryScore: 7.1,
    servingSize: '1 cup (240ml)',
    frequency: '2-3 cups daily',
    keyCompounds: ['Aspalathin', 'Quercetin', 'Orientin'],
    cost: '$10-15/lb',
    preparation: 'Steep 5-7 min in boiling water',
    description: 'Caffeine-free South African tea',
    mechanisms: ['Unique antioxidant activity', 'Blood sugar support'],
    sideEffects: 'Very safe, caffeine-free',
    interactions: []
  },
  {
    id: 'pomegranate_juice',
    name: 'Pure Pomegranate Juice',
    category: 'Beverages',
    evidence: { score: 8.2, studies: 92 },
    antiInflammatoryScore: 8.0,
    servingSize: '4 oz (120ml)',
    frequency: 'Several times per week',
    keyCompounds: ['Punicalagins', 'Anthocyanins', 'Ellagic acid'],
    cost: '$8-12/bottle',
    preparation: '100% juice, no added sugars',
    description: 'Antioxidant-rich juice',
    mechanisms: ['NO production', 'Antioxidant activity'],
    sideEffects: 'High natural sugar content',
    interactions: []
  },
  {
    id: 'tart_cherry_juice',
    name: 'Tart Cherry Juice',
    category: 'Beverages',
    evidence: { score: 7.9, studies: 54 },
    antiInflammatoryScore: 8.2,
    servingSize: '8 oz (240ml)',
    frequency: 'Daily for recovery',
    keyCompounds: ['Anthocyanins', 'Melatonin', 'Phenolic acids'],
    cost: '$15-20/bottle',
    preparation: '100% tart cherry, dilute if needed',
    description: 'Exercise recovery and sleep support',
    mechanisms: ['COX inhibition', 'Muscle recovery'],
    sideEffects: 'Natural sugar content',
    interactions: []
  },
  {
    id: 'bone_broth',
    name: 'Bone Broth',
    category: 'Beverages',
    evidence: { score: 7.0, studies: 28 },
    antiInflammatoryScore: 6.8,
    servingSize: '1 cup (240ml)',
    frequency: 'Several times per week',
    keyCompounds: ['Collagen', 'Glycine', 'Glutamine', 'Minerals'],
    cost: '$5-8/quart',
    preparation: 'Simmer 12-24 hours, season to taste',
    description: 'Traditional gut-healing beverage',
    mechanisms: ['Gut lining support', 'Joint support'],
    sideEffects: 'High sodium in some preparations',
    interactions: []
  },

  // CRUCIFEROUS VEGETABLES (10 foods)
  {
    id: 'broccoli',
    name: 'Broccoli',
    category: 'Cruciferous',
    evidence: { score: 8.4, studies: 167 },
    antiInflammatoryScore: 8.1,
    servingSize: '1 cup chopped (91g)',
    frequency: 'Daily',
    keyCompounds: ['Sulforaphane', 'Glucosinolates', 'Vitamin C', 'Folate'],
    cost: '$2-4/head',
    preparation: 'Steam, roast, or raw - chop and wait 10 min',
    description: 'Detox powerhouse with sulforaphane',
    mechanisms: ['Nrf2 pathway activation', 'Phase II enzyme induction'],
    sideEffects: 'May cause gas in some',
    interactions: []
  },
  {
    id: 'brussels_sprouts',
    name: 'Brussels Sprouts',
    category: 'Cruciferous',
    evidence: { score: 8.0, studies: 72 },
    antiInflammatoryScore: 7.9,
    servingSize: '1 cup (88g)',
    frequency: 'Several times per week',
    keyCompounds: ['Glucosinolates', 'Vitamin K', 'Vitamin C', 'Fiber'],
    cost: '$3-5/lb',
    preparation: 'Roasted, sautéed, or shaved raw',
    description: 'Mini cabbages with detox compounds',
    mechanisms: ['Glucosinolate activation', 'Antioxidant support'],
    sideEffects: 'May cause gas',
    interactions: []
  },
  {
    id: 'cauliflower',
    name: 'Cauliflower',
    category: 'Cruciferous',
    evidence: { score: 7.8, studies: 84 },
    antiInflammatoryScore: 7.5,
    servingSize: '1 cup chopped (107g)',
    frequency: 'Several times per week',
    keyCompounds: ['Glucosinolates', 'Vitamin C', 'Choline', 'Fiber'],
    cost: '$2-4/head',
    preparation: 'Roasted, riced, or mashed',
    description: 'Versatile cruciferous vegetable',
    mechanisms: ['Detoxification support', 'Choline for brain health'],
    sideEffects: 'May cause gas',
    interactions: []
  },
  {
    id: 'cabbage',
    name: 'Cabbage (Red/Purple)',
    category: 'Cruciferous',
    evidence: { score: 7.9, studies: 68 },
    antiInflammatoryScore: 7.7,
    servingSize: '1 cup shredded (89g)',
    frequency: 'Several times per week',
    keyCompounds: ['Anthocyanins', 'Glucosinolates', 'Vitamin C', 'Vitamin K'],
    cost: '$1-2/lb',
    preparation: 'Raw in slaws, fermented, or cooked',
    description: 'Affordable cruciferous with anthocyanins',
    mechanisms: ['Antioxidant activity', 'Gut health via fermentation'],
    sideEffects: 'May cause gas',
    interactions: []
  },
  {
    id: 'bok_choy',
    name: 'Bok Choy',
    category: 'Cruciferous',
    evidence: { score: 7.6, studies: 42 },
    antiInflammatoryScore: 7.4,
    servingSize: '1 cup chopped (70g)',
    frequency: 'Several times per week',
    keyCompounds: ['Glucosinolates', 'Vitamin A', 'Vitamin C', 'Calcium'],
    cost: '$2-4/lb',
    preparation: 'Stir-fried, steamed, or in soups',
    description: 'Asian cruciferous with mild flavor',
    mechanisms: ['Glucosinolate metabolism', 'Calcium signaling'],
    sideEffects: 'Very safe',
    interactions: []
  },
  {
    id: 'radishes',
    name: 'Radishes',
    category: 'Cruciferous',
    evidence: { score: 7.2, studies: 28 },
    antiInflammatoryScore: 7.0,
    servingSize: '1/2 cup sliced (58g)',
    frequency: 'Several times per week',
    keyCompounds: ['Glucosinolates', 'Vitamin C', 'Anthocyanins'],
    cost: '$2-3/bunch',
    preparation: 'Raw in salads, pickled, or roasted',
    description: 'Crunchy cruciferous with bite',
    mechanisms: ['Detoxification support', 'Digestive stimulation'],
    sideEffects: 'Very safe',
    interactions: []
  },
  {
    id: 'kohlrabi',
    name: 'Kohlrabi',
    category: 'Cruciferous',
    evidence: { score: 7.1, studies: 22 },
    antiInflammatoryScore: 7.0,
    servingSize: '1 cup (135g)',
    frequency: 'Several times per week',
    keyCompounds: ['Glucosinolates', 'Vitamin C', 'Fiber', 'Potassium'],
    cost: '$2-4/bulb',
    preparation: 'Raw, roasted, or in slaws',
    description: 'Alien-looking cruciferous vegetable',
    mechanisms: ['Glucosinolate activation', 'Fiber fermentation'],
    sideEffects: 'Very safe',
    interactions: []
  },
  {
    id: 'turnips',
    name: 'Turnips',
    category: 'Cruciferous',
    evidence: { score: 7.0, studies: 24 },
    antiInflammatoryScore: 6.9,
    servingSize: '1 cup cubed (130g)',
    frequency: 'Several times per week',
    keyCompounds: ['Glucosinolates', 'Vitamin C', 'Fiber'],
    cost: '$1-2/lb',
    preparation: 'Roasted, mashed, or in soups',
    description: 'Root cruciferous vegetable',
    mechanisms: ['Detoxification support', 'Prebiotic effects'],
    sideEffects: 'Very safe',
    interactions: []
  },
  {
    id: 'broccoli_sprouts',
    name: 'Broccoli Sprouts',
    category: 'Cruciferous',
    evidence: { score: 8.8, studies: 58 },
    antiInflammatoryScore: 9.0,
    servingSize: '1/4 cup (25g)',
    frequency: 'Several times per week',
    keyCompounds: ['Sulforaphane', 'Glucoraphanin', 'Vitamin C'],
    cost: '$5-8/container',
    preparation: 'Raw in salads, sandwiches, smoothies',
    description: 'Concentrated sulforaphane source',
    mechanisms: ['Potent Nrf2 activation', 'Detoxification enzyme induction'],
    sideEffects: 'Very safe',
    interactions: []
  },
  {
    id: 'rutabaga',
    name: 'Rutabaga',
    category: 'Cruciferous',
    evidence: { score: 6.9, studies: 18 },
    antiInflammatoryScore: 6.8,
    servingSize: '1 cup cubed (140g)',
    frequency: 'Several times per week',
    keyCompounds: ['Glucosinolates', 'Vitamin C', 'Potassium'],
    cost: '$1-2/lb',
    preparation: 'Roasted, mashed, or in stews',
    description: 'Cross between cabbage and turnip',
    mechanisms: ['Glucosinolate metabolism', 'Antioxidant activity'],
    sideEffects: 'Very safe',
    interactions: []
  },

  // MUSHROOMS (7 foods - adding the missing category)
  {
    id: 'lions_mane',
    name: "Lion's Mane Mushroom",
    category: 'Mushrooms',
    evidence: { score: 8.4, studies: 67 },
    antiInflammatoryScore: 8.3,
    servingSize: '100g fresh or 3g powder',
    frequency: 'Daily',
    keyCompounds: ['Hericenones', 'Erinacines', 'Beta-glucans', 'Polysaccharides'],
    cost: '$15-25/lb fresh',
    preparation: 'Sautéed, roasted, or as supplement powder',
    description: 'Cognitive support mushroom with neuroinflammation benefits',
    mechanisms: ['NGF stimulation', 'Microglial modulation', 'BDNF increase'],
    sideEffects: 'Very safe, mild digestive effects initially',
    interactions: []
  },
  {
    id: 'turkey_tail',
    name: 'Turkey Tail Mushroom',
    category: 'Mushrooms',
    evidence: { score: 8.8, studies: 124 },
    antiInflammatoryScore: 8.5,
    servingSize: '1-3g powder',
    frequency: 'Daily',
    keyCompounds: ['PSK', 'PSP', 'Beta-glucans', 'Polysaccharides'],
    cost: '$20-30/100g powder',
    preparation: 'Tea, powder in smoothies, or capsules',
    description: 'Immune-modulating mushroom with extensive research',
    mechanisms: ['Immune system modulation', 'Gut microbiome support'],
    sideEffects: 'Very safe',
    interactions: []
  },
  {
    id: 'reishi',
    name: 'Reishi Mushroom',
    category: 'Mushrooms',
    evidence: { score: 8.9, studies: 156 },
    antiInflammatoryScore: 8.7,
    servingSize: '1.5-3g extract',
    frequency: 'Daily',
    keyCompounds: ['Ganoderic acids', 'Beta-glucans', 'Triterpenes', 'Polysaccharides'],
    cost: '$25-40/100g powder',
    preparation: 'Tea, tincture, or powder',
    description: 'The "mushroom of immortality" with broad anti-inflammatory effects',
    mechanisms: ['NF-κB inhibition', 'Immune modulation', 'Liver protection'],
    sideEffects: 'May cause dry mouth',
    interactions: []
  },
  {
    id: 'chaga',
    name: 'Chaga Mushroom',
    category: 'Mushrooms',
    evidence: { score: 8.1, studies: 78 },
    antiInflammatoryScore: 8.2,
    servingSize: '1-2g powder',
    frequency: 'Daily',
    keyCompounds: ['Betulinic acid', 'Inotodiol', 'Beta-glucans', 'Melanin'],
    cost: '$30-50/100g powder',
    preparation: 'Tea or powder',
    description: 'Antioxidant-rich mushroom from birch trees',
    mechanisms: ['Oxidative stress reduction', 'Cytokine modulation'],
    sideEffects: 'May interact with blood thinners',
    interactions: ['anticoagulants']
  },
  {
    id: 'cordyceps',
    name: 'Cordyceps',
    category: 'Mushrooms',
    evidence: { score: 8.3, studies: 92 },
    antiInflammatoryScore: 8.0,
    servingSize: '1-3g powder',
    frequency: 'Daily',
    keyCompounds: ['Cordycepin', 'Adenosine', 'Polysaccharides', 'Beta-glucans'],
    cost: '$40-60/100g powder',
    preparation: 'Powder in beverages or capsules',
    description: 'Energy and endurance mushroom with anti-inflammatory properties',
    mechanisms: ['ATP production', 'Inflammatory cytokine reduction'],
    sideEffects: 'May cause mild GI upset initially',
    interactions: []
  },
  {
    id: 'shiitake',
    name: 'Shiitake Mushrooms',
    category: 'Mushrooms',
    evidence: { score: 8.5, studies: 145 },
    antiInflammatoryScore: 8.1,
    servingSize: '100g fresh or 15g dried',
    frequency: '2-3 times per week',
    keyCompounds: ['Lentinan', 'Eritadenine', 'Beta-glucans', 'Vitamin D'],
    cost: '$10-15/lb fresh',
    preparation: 'Sautéed, grilled, or in soups',
    description: 'Culinary mushroom with medicinal properties',
    mechanisms: ['Immune enhancement', 'Cholesterol reduction'],
    sideEffects: 'Raw consumption may cause dermatitis',
    interactions: []
  },
  {
    id: 'maitake',
    name: 'Maitake Mushrooms',
    category: 'Mushrooms',
    evidence: { score: 8.2, studies: 89 },
    antiInflammatoryScore: 8.0,
    servingSize: '100g fresh or 3-7g dried',
    frequency: '2-3 times per week',
    keyCompounds: ['D-fraction', 'Beta-glucans', 'Polysaccharides', 'Ergothioneine'],
    cost: '$15-20/lb fresh',
    preparation: 'Roasted, sautéed, or in broths',
    description: 'Dancing mushroom with immune and metabolic benefits',
    mechanisms: ['Blood sugar regulation', 'Immune activation'],
    sideEffects: 'May lower blood sugar',
    interactions: ['diabetes medications']
  },

  // CITRUS FRUITS (5 foods)
  {
    id: 'lemon',
    name: 'Lemons',
    category: 'Citrus',
    evidence: { score: 7.8, studies: 67 },
    antiInflammatoryScore: 7.5,
    servingSize: '1 medium lemon (60g)',
    frequency: 'Daily',
    keyCompounds: ['Vitamin C', 'Limonene', 'Hesperidin', 'Diosmin'],
    cost: '$0.50-1/lemon',
    preparation: 'Juice in water, zest, or dressing',
    description: 'Alkalizing citrus with vitamin C and flavonoids',
    mechanisms: ['Antioxidant activity', 'pH balance', 'Liver detoxification'],
    sideEffects: 'May erode tooth enamel if consumed undiluted',
    interactions: []
  },
  {
    id: 'orange',
    name: 'Oranges',
    category: 'Citrus',
    evidence: { score: 8.0, studies: 112 },
    antiInflammatoryScore: 7.6,
    servingSize: '1 medium orange (154g)',
    frequency: 'Daily',
    keyCompounds: ['Vitamin C', 'Hesperidin', 'Naringenin', 'Beta-cryptoxanthin'],
    cost: '$0.50-1.50/orange',
    preparation: 'Fresh, juiced, or zested',
    description: 'Classic citrus packed with anti-inflammatory flavonoids',
    mechanisms: ['Immune support', 'Collagen synthesis', 'Flavonoid activity'],
    sideEffects: 'High sugar content in juice form',
    interactions: []
  },
  {
    id: 'grapefruit',
    name: 'Grapefruit',
    category: 'Citrus',
    evidence: { score: 7.9, studies: 89 },
    antiInflammatoryScore: 7.8,
    servingSize: 'Half grapefruit (123g)',
    frequency: 'Several times per week',
    keyCompounds: ['Vitamin C', 'Naringin', 'Lycopene (pink)', 'Limonoids'],
    cost: '$1-2/fruit',
    preparation: 'Fresh halves, segments, or juice',
    description: 'Powerful citrus with unique enzyme interactions',
    mechanisms: ['Metabolic enhancement', 'Insulin sensitivity'],
    sideEffects: 'Interacts with many medications',
    interactions: ['statins', 'blood pressure meds', 'various drugs']
  },
  {
    id: 'lime',
    name: 'Limes',
    category: 'Citrus',
    evidence: { score: 7.4, studies: 45 },
    antiInflammatoryScore: 7.2,
    servingSize: '1 medium lime (67g)',
    frequency: 'Daily',
    keyCompounds: ['Vitamin C', 'Limonene', 'Flavonoids', 'Citric acid'],
    cost: '$0.33-0.75/lime',
    preparation: 'Juice, zest, or wedges',
    description: 'Tart citrus with alkalizing properties',
    mechanisms: ['Antioxidant activity', 'Iron absorption enhancement'],
    sideEffects: 'May cause photosensitivity on skin',
    interactions: []
  },
  {
    id: 'tangerine',
    name: 'Tangerines/Mandarins',
    category: 'Citrus',
    evidence: { score: 7.6, studies: 56 },
    antiInflammatoryScore: 7.3,
    servingSize: '1 medium (88g)',
    frequency: 'Daily',
    keyCompounds: ['Vitamin C', 'Nobiletin', 'Tangeretin', 'Beta-carotene'],
    cost: '$0.50-1/fruit',
    preparation: 'Fresh segments or juice',
    description: 'Sweet citrus with unique polymethoxylated flavones',
    mechanisms: ['Cholesterol reduction', 'Neuroprotection'],
    sideEffects: 'Generally very safe',
    interactions: []
  },

  // OTHER ANTI-INFLAMMATORY FOODS (8 foods)
  {
    id: 'dark_chocolate',
    name: 'Dark Chocolate (70%+)',
    category: 'Other',
    evidence: { score: 8.5, studies: 156 },
    antiInflammatoryScore: 8.2,
    servingSize: '1 oz (28g)',
    frequency: 'Daily',
    keyCompounds: ['Flavanols', 'Theobromine', 'Phenylethylamine', 'Anandamide'],
    cost: '$0.50-2/oz',
    preparation: 'Eat directly, melt into beverages',
    description: 'Antioxidant-rich treat with cardiovascular benefits',
    mechanisms: ['NO production', 'Platelet aggregation inhibition', 'Mood enhancement'],
    sideEffects: 'Contains caffeine, high calorie',
    interactions: []
  },
  {
    id: 'tomatoes',
    name: 'Tomatoes',
    category: 'Other',
    evidence: { score: 8.3, studies: 234 },
    antiInflammatoryScore: 7.9,
    servingSize: '1 medium tomato (123g)',
    frequency: 'Daily',
    keyCompounds: ['Lycopene', 'Vitamin C', 'Beta-carotene', 'Vitamin E'],
    cost: '$2-4/lb',
    preparation: 'Raw, cooked with oil for lycopene absorption',
    description: 'Lycopene-rich fruit vegetable with proven benefits',
    mechanisms: ['Antioxidant activity', 'UV protection', 'Prostate health'],
    sideEffects: 'Nightshade - may trigger inflammation in sensitive individuals',
    interactions: []
  },
  {
    id: 'oats',
    name: 'Oats',
    category: 'Other',
    evidence: { score: 8.1, studies: 189 },
    antiInflammatoryScore: 7.7,
    servingSize: '1/2 cup dry (40g)',
    frequency: 'Daily',
    keyCompounds: ['Beta-glucan', 'Avenanthramides', 'Fiber', 'Manganese'],
    cost: '$3-5/lb',
    preparation: 'Oatmeal, overnight oats, baked goods',
    description: 'Heart-healthy grain with unique antioxidants',
    mechanisms: ['Cholesterol reduction', 'Blood sugar regulation', 'Gut health'],
    sideEffects: 'May contain gluten traces',
    interactions: []
  },
  {
    id: 'yogurt',
    name: 'Yogurt (Probiotic)',
    category: 'Other',
    evidence: { score: 8.2, studies: 267 },
    antiInflammatoryScore: 7.8,
    servingSize: '1 cup (245g)',
    frequency: 'Daily',
    keyCompounds: ['Probiotics', 'Protein', 'Calcium', 'B vitamins'],
    cost: '$3-6/32oz',
    preparation: 'Plain, unsweetened preferred',
    description: 'Fermented dairy with gut-inflammation benefits',
    mechanisms: ['Gut microbiome support', 'Immune modulation', 'Intestinal barrier'],
    sideEffects: 'Dairy sensitivity in some',
    interactions: []
  },
  {
    id: 'pomegranate',
    name: 'Pomegranate',
    category: 'Other',
    evidence: { score: 8.6, studies: 145 },
    antiInflammatoryScore: 8.4,
    servingSize: '1/2 cup arils (87g)',
    frequency: '2-3 times per week',
    keyCompounds: ['Punicalagins', 'Anthocyanins', 'Ellagic acid', 'Vitamin C'],
    cost: '$2-5/fruit',
    preparation: 'Fresh arils, juice (unsweetened)',
    description: 'Antioxidant powerhouse fruit',
    mechanisms: ['NO synthesis', 'NF-κB inhibition', 'Cardiovascular protection'],
    sideEffects: 'May interact with blood pressure meds',
    interactions: ['ACE inhibitors']
  },
  {
    id: 'beets',
    name: 'Beets',
    category: 'Other',
    evidence: { score: 7.9, studies: 98 },
    antiInflammatoryScore: 7.8,
    servingSize: '1 cup (136g)',
    frequency: '2-3 times per week',
    keyCompounds: ['Betalains', 'Nitrates', 'Folate', 'Manganese'],
    cost: '$2-3/lb',
    preparation: 'Roasted, juiced, or pickled',
    description: 'Nitrate-rich root with exercise benefits',
    mechanisms: ['NO production', 'Blood flow enhancement', 'Detoxification'],
    sideEffects: 'May color urine red',
    interactions: []
  },
  {
    id: 'sweet_potato',
    name: 'Sweet Potatoes',
    category: 'Other',
    evidence: { score: 7.8, studies: 87 },
    antiInflammatoryScore: 7.5,
    servingSize: '1 medium (114g)',
    frequency: 'Several times per week',
    keyCompounds: ['Beta-carotene', 'Anthocyanins (purple)', 'Fiber', 'Vitamin A'],
    cost: '$1-2/lb',
    preparation: 'Baked, roasted, or mashed',
    description: 'Nutrient-dense root vegetable',
    mechanisms: ['Vitamin A activity', 'Blood sugar regulation', 'Gut health'],
    sideEffects: 'High in carbohydrates',
    interactions: []
  },
  {
    id: 'mushrooms',
    name: 'Mixed Culinary Mushrooms',
    category: 'Other',
    evidence: { score: 7.5, studies: 78 },
    antiInflammatoryScore: 7.2,
    servingSize: '1 cup (70g)',
    frequency: '2-3 times per week',
    keyCompounds: ['Ergothioneine', 'Selenium', 'B vitamins', 'Vitamin D (UV-exposed)'],
    cost: '$4-8/lb',
    preparation: 'Sautéed, grilled, or roasted',
    description: 'General culinary mushrooms (button, portobello, etc.)',
    mechanisms: ['Immune support', 'Antioxidant activity'],
    sideEffects: 'Cook thoroughly',
    interactions: []
  },
  {
    id: 'bell_peppers',
    name: 'Bell Peppers',
    category: 'Other',
    evidence: { score: 7.9, studies: 89 },
    antiInflammatoryScore: 7.7,
    servingSize: '1 medium pepper (119g)',
    frequency: 'Daily',
    keyCompounds: ['Vitamin C', 'Quercetin', 'Luteolin', 'Beta-carotene'],
    cost: '$1-3/pepper',
    preparation: 'Raw, roasted, grilled, or sautéed',
    description: 'Colorful peppers rich in vitamin C and quercetin',
    mechanisms: ['Antioxidant activity', 'Quercetin anti-inflammatory action'],
    sideEffects: 'Nightshade - may affect sensitive individuals',
    interactions: []
  },
  {
    id: 'chili_peppers',
    name: 'Chili Peppers',
    category: 'Spices & Herbs',
    evidence: { score: 8.4, studies: 178 },
    antiInflammatoryScore: 8.2,
    servingSize: '1-2 peppers or 1/4 tsp powder',
    frequency: 'Daily',
    keyCompounds: ['Capsaicin', 'Vitamin C', 'Beta-carotene', 'Flavonoids'],
    cost: '$2-5/lb fresh',
    preparation: 'Fresh, dried, or powdered in dishes',
    description: 'Capsaicin-rich peppers with pain-relieving properties',
    mechanisms: ['Substance P depletion', 'TRPV1 activation', 'Metabolic boost'],
    sideEffects: 'May cause digestive discomfort',
    interactions: []
  }
];

// Food synergies (positive interactions)
const synergies = [
  {
    food1: 'turmeric',
    food2: 'black_pepper',
    type: 'absorption',
    description: 'Black pepper increases curcumin absorption by 2000%',
    recommendation: 'Always combine turmeric with black pepper',
    evidenceLevel: 9
  },
  {
    food1: 'turmeric',
    food2: 'olive_oil',
    type: 'absorption',
    description: 'Fat enhances curcumin absorption significantly',
    recommendation: 'Combine turmeric with healthy fats',
    evidenceLevel: 8
  },
  {
    food1: 'spinach',
    food2: 'avocado',
    type: 'absorption',
    description: 'Fat increases carotenoid absorption by 300-500%',
    recommendation: 'Add avocado to leafy green salads',
    evidenceLevel: 8
  },
  {
    food1: 'kale',
    food2: 'olive_oil',
    type: 'absorption',
    description: 'Fat-soluble vitamins better absorbed with oil',
    recommendation: 'Drizzle olive oil on cooked greens',
    evidenceLevel: 8
  },
  {
    food1: 'green_tea',
    food2: 'blueberries',
    type: 'antioxidant',
    description: 'Catechins and anthocyanins work synergistically',
    recommendation: 'Enjoy green tea with berry snacks',
    evidenceLevel: 7
  },
  {
    food1: 'salmon',
    food2: 'walnuts',
    type: 'omega3',
    description: 'Marine and plant omega-3s provide comprehensive coverage',
    recommendation: 'Combine different omega-3 sources',
    evidenceLevel: 8
  },
  {
    food1: 'broccoli',
    food2: 'olive_oil',
    type: 'absorption',
    description: 'Oil enhances sulforaphane absorption',
    recommendation: 'Lightly sauté or drizzle oil on broccoli',
    evidenceLevel: 7
  },
  {
    food1: 'garlic',
    food2: 'ginger',
    type: 'synergy',
    description: 'Combined anti-inflammatory effects amplified',
    recommendation: 'Use together in cooking',
    evidenceLevel: 7
  },
  {
    food1: 'tart_cherries',
    food2: 'walnuts',
    type: 'recovery',
    description: 'Enhanced exercise recovery when combined',
    recommendation: 'Post-workout snack combination',
    evidenceLevel: 6
  },
  {
    food1: 'matcha',
    food2: 'ginger',
    type: 'metabolism',
    description: 'Enhanced thermogenic and anti-inflammatory effects',
    recommendation: 'Matcha latte with ginger',
    evidenceLevel: 6
  }
];

// Diet templates for common goals
const dietTemplates = [
  {
    id: 'mediterranean',
    name: 'Mediterranean Anti-Inflammatory',
    foods: ['olive_oil', 'salmon', 'walnuts', 'spinach', 'blueberries', 'garlic', 'tomatoes'],
    description: 'Evidence-based Mediterranean pattern for longevity',
    popularity: 95,
    icon: 'heart'
  },
  {
    id: 'brain_health',
    name: 'Brain Health Foods',
    foods: ['blueberries', 'salmon', 'walnuts', 'green_tea', 'turmeric', 'broccoli', 'dark_chocolate'],
    description: 'Cognitive protection and neuroinflammation reduction',
    popularity: 85,
    icon: 'brain'
  },
  {
    id: 'joint_support',
    name: 'Joint Support Foods',
    foods: ['tart_cherries', 'salmon', 'turmeric', 'ginger', 'kale', 'olive_oil', 'bone_broth'],
    description: 'Natural foods for joint inflammation relief',
    popularity: 80,
    icon: 'activity'
  },
  {
    id: 'heart_healthy',
    name: 'Heart-Healthy Stack',
    foods: ['olive_oil', 'sardines', 'flaxseeds', 'avocado', 'green_tea', 'garlic', 'oats'],
    description: 'Cardiovascular inflammation protection',
    popularity: 90,
    icon: 'heart'
  },
  {
    id: 'gut_health',
    name: 'Gut Healing Foods',
    foods: ['bone_broth', 'ginger', 'turmeric', 'cabbage', 'garlic', 'green_tea', 'flaxseeds'],
    description: 'Foods to reduce gut inflammation',
    popularity: 75,
    icon: 'target'
  },
  {
    id: 'athletic_recovery',
    name: 'Athletic Recovery',
    foods: ['tart_cherries', 'salmon', 'turmeric', 'ginger', 'blueberries', 'walnuts', 'green_tea'],
    description: 'Reduce exercise-induced inflammation',
    popularity: 70,
    icon: 'zap'
  },
  {
    id: 'autoimmune',
    name: 'Autoimmune Support',
    foods: ['salmon', 'turmeric', 'ginger', 'green_tea', 'broccoli', 'olive_oil', 'mushrooms'],
    description: 'Foods to modulate immune response',
    popularity: 65,
    icon: 'shield'
  },
  {
    id: 'beginner',
    name: 'Beginner Anti-Inflammatory',
    foods: ['salmon', 'blueberries', 'spinach', 'olive_oil', 'green_tea'],
    description: 'Easy-to-find anti-inflammatory foods',
    popularity: 100,
    icon: 'star'
  }
];

// Export for use in application
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { foods, synergies, dietTemplates };
}